import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  activateBetaPro,
  getAccountUpgradeContext,
} from '../../utils/mindmap/apiClient';

const CAPABILITY_LABELS = {
  'maps.active.max': 'Create another active map',
  'collaboration.named_editors.max': 'Add another editor',
  'collaboration.editor_link': 'Let anyone with the link edit',
};

const COMPARISON_ROWS = [
  ['Active maps', '3', 'Unlimited'],
  ['Named editors per map', '1', 'Unlimited'],
  ['Anyone-with-link access', 'View', 'View or edit'],
];

function formatDate(value) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'long',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function UpgradeDialog({
  isOpen,
  capabilityKey = null,
  isDarkMode = true,
  onClose,
  onActivated,
  returnLabel = null,
}) {
  const titleRef = useRef(null);
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [context, setContext] = useState(null);
  const [stage, setStage] = useState('details');
  const [isLoading, setIsLoading] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    previousFocusRef.current = document.activeElement;
    setContext(null);
    setStage('details');
    setError(null);
    setIsLoading(true);
    let cancelled = false;
    getAccountUpgradeContext(capabilityKey)
      .then((result) => {
        if (!cancelled) setContext(result);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || 'Plan details could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    const focusTimer = setTimeout(() => titleRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(panelRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || []);
      if (focusable.length === 0) {
        event.preventDefault();
        titleRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelled = true;
      clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [capabilityKey, isOpen]);

  const account = context?.account || null;
  const betaPro = account?.betaPro || null;
  const requestedLabel = CAPABILITY_LABELS[capabilityKey] || null;
  const expiryLabel = formatDate(betaPro?.expiresAt || betaPro?.prospectiveExpiresAt);
  const palette = useMemo(() => ({
    panel: isDarkMode ? '#18181b' : '#ffffff',
    panelMuted: isDarkMode ? '#27272a' : '#f4f4f5',
    border: isDarkMode ? '#52525b' : '#d4d4d8',
    text: isDarkMode ? '#fafafa' : '#18181b',
    muted: isDarkMode ? '#a1a1aa' : '#52525b',
    accent: '#2563eb',
    success: isDarkMode ? '#86efac' : '#166534',
  }), [isDarkMode]);

  if (!isOpen) return null;

  const handleActivate = async () => {
    setIsActivating(true);
    setError(null);
    try {
      const result = await activateBetaPro();
      if (!result?.account) throw new Error('Activation returned no account details.');
      setContext((previous) => ({
        ...(previous || {}),
        account: result.account,
      }));
      window.dispatchEvent(new CustomEvent('mindmappi:accountCapabilitiesChanged', {
        detail: result.account,
      }));
      setStage('success');
    } catch (activationError) {
      setError(activationError.message || 'Beta Pro could not be activated.');
      setStage('confirm');
    } finally {
      setIsActivating(false);
    }
  };

  const handleContinue = async () => {
    setError(null);
    try {
      if (onActivated) await onActivated(context?.account || null);
      onClose();
    } catch (continueError) {
      setError(continueError.message || 'The original action could not be completed.');
    }
  };

  const buttonStyle = {
    border: 0,
    borderRadius: 999,
    padding: '10px 16px',
    background: palette.accent,
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return createPortal(
    <div
      data-upgrade-dialog-overlay="true"
      role="presentation"
      onMouseDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 420,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(0, 0, 0, 0.58)',
      }}
    >
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-dialog-title"
        aria-describedby="upgrade-dialog-description"
        onMouseDown={(event) => event.stopPropagation()}
        style={{
          width: 'min(620px, calc(100vw - 24px))',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          boxSizing: 'border-box',
          borderRadius: 16,
          padding: 'clamp(18px, 4vw, 28px)',
          background: palette.panel,
          color: palette.text,
          border: `1px solid ${palette.border}`,
          boxShadow: '0 28px 80px rgba(0,0,0,0.45)',
          fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ color: palette.muted, fontSize: 12, fontWeight: 700 }}>
              {account?.plan?.label || 'Plan & features'}
            </div>
            <h2
              id="upgrade-dialog-title"
              ref={titleRef}
              tabIndex="-1"
              style={{ margin: '4px 0 0', fontSize: 24, outline: 'none' }}
            >
              {stage === 'success' ? 'Beta Pro is active' : 'Free and Beta Pro'}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close plan dialog"
            onClick={onClose}
            style={{
              ...buttonStyle,
              padding: '6px 10px',
              background: palette.panelMuted,
              color: palette.text,
            }}
          >
            ×
          </button>
        </div>

        <p id="upgrade-dialog-description" style={{ color: palette.muted, lineHeight: 1.5 }}>
          {requestedLabel
            ? `${requestedLabel} is a Pro capability. Your work stays intact if Pro later expires.`
            : 'Compare the collaboration limits included in each plan.'}
        </p>

        {isLoading && <p role="status">Loading plan details…</p>}

        {!isLoading && account && stage === 'details' && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr>
                    <th scope="col" style={{ padding: 10, textAlign: 'left' }}>Capability</th>
                    <th scope="col" style={{ padding: 10, textAlign: 'left' }}>Free</th>
                    <th scope="col" style={{ padding: 10, textAlign: 'left' }}>Beta Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map(([label, free, pro]) => (
                    <tr key={label} style={{ borderTop: `1px solid ${palette.border}` }}>
                      <th scope="row" style={{ padding: 10, textAlign: 'left' }}>{label}</th>
                      <td style={{ padding: 10 }}>{free}</td>
                      <td style={{ padding: 10, color: palette.success, fontWeight: 700 }}>{pro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {betaPro?.active ? (
              <div role="status" style={{ marginTop: 18, color: palette.success, fontWeight: 700 }}>
                Beta Pro is active{expiryLabel ? ` until ${expiryLabel}` : ''}.
              </div>
            ) : betaPro?.activationEnabled && betaPro?.eligible ? (
              <button type="button" style={{ ...buttonStyle, marginTop: 18 }} onClick={() => setStage('confirm')}>
                {betaPro.offer?.actionLabel || 'Activate Beta Pro'}
              </button>
            ) : (
              <div role="status" style={{ marginTop: 18, color: palette.muted }}>
                Beta Pro self-service activation is not available for this account yet.
              </div>
            )}
          </>
        )}

        {!isLoading && stage === 'confirm' && (
          <div style={{ padding: 16, borderRadius: 12, background: palette.panelMuted }}>
            <h3 style={{ marginTop: 0 }}>Confirm complimentary Beta Pro</h3>
            <p style={{ lineHeight: 1.5 }}>
              Activate {betaPro?.entitlementMonths || 6} calendar months of Beta Pro
              {expiryLabel ? `, ending ${expiryLabel}` : ''}. No card or payment details
              are required, and no charge or automatic renewal will occur.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button
                type="button"
                style={buttonStyle}
                disabled={isActivating}
                onClick={handleActivate}
              >
                {isActivating ? 'Activating…' : 'Confirm activation'}
              </button>
              <button
                type="button"
                style={{ ...buttonStyle, background: palette.panel, color: palette.text, border: `1px solid ${palette.border}` }}
                disabled={isActivating}
                onClick={() => setStage('details')}
              >
                Back
              </button>
            </div>
          </div>
        )}

        {!isLoading && stage === 'success' && (
          <div role="status" aria-live="polite" style={{ padding: 16, borderRadius: 12, background: palette.panelMuted }}>
            <p style={{ marginTop: 0, color: palette.success, fontWeight: 700 }}>
              Your Pro capabilities are ready{formatDate(context?.account?.betaPro?.expiresAt)
                ? ` until ${formatDate(context.account.betaPro.expiresAt)}`
                : ''}.
            </p>
            <button type="button" style={buttonStyle} onClick={handleContinue}>
              {returnLabel ? `Continue: ${returnLabel}` : 'Done'}
            </button>
          </div>
        )}

        {error && (
          <div role="alert" style={{ marginTop: 14, color: '#f87171' }}>
            {error}{' '}
            {stage === 'details' && (
              <button type="button" onClick={() => {
                setError(null);
                setIsLoading(true);
                getAccountUpgradeContext(capabilityKey)
                  .then(setContext)
                  .catch((retryError) => setError(retryError.message))
                  .finally(() => setIsLoading(false));
              }}
              >
                Retry
              </button>
            )}
          </div>
        )}
      </section>
    </div>,
    document.body
  );
}
